import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import ViewIcon from '@mui/icons-material/Article';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { formatDate } from 'date-fns';
import usePolling from '@eballoi/react-use-polling';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import ErrorMessage from '@/components/errorMessage';
import TextField from '@/components/textField';
import DataGridTable from '@/components/datagridTable/datagridTable';
import SelectField from '../selectField';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useUserContext } from '@/contexts/userContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';

type Props = { agentInstance: any; maxHeight: number; onOpenItem: (item: any) => void };

const ITEM_STATUSES = ['SCANNED', 'PROCESSING', 'PROCESSED', 'FAILED'];

const ItemProcessingStatuses = ({ agentInstance = {}, maxHeight, onOpenItem }: Props) => {
  const apiContext = useApiContext();
  const { hasPermission } = useUserContext();
  const { toast } = useNotificationContext();

  const [headerRef, headerSize] = useElementSize();

  const [loading, setLoading] = useState(false);
  const [executingWhole, setExecutingWhole] = useState(false);
  const [executingRowId, setExecutingRowId] = useState<string | null>(null);
  const [itemName, setItemName] = useState('');
  const [status, setStatus] = useState('');
  const [appliedItemName, setAppliedItemName] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');

  const [showFiters, setShowFiters] = useState(false);

  const [itemProcessingStatuses, setItemProcessingStatuses] = useState<any[]>([]);
  const [error, setError] = useState<any>();
  const [suppressEvents, setSuppressEvents] = useState<boolean>(false);

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1;

  const viewProcesseItems = hasPermission(`pipeline:agents:items:view`, agentInstance?.userAccess);

  const handleExecuteWhole = async () => {
    if (!agentInstance.agentDefId || !agentInstance.id) return;
    setExecutingWhole(true);
    setError(null);
    try {
      const res = await apiContext.executeAgentInstance(agentInstance.agentDefId, agentInstance.id, {
        suppressEvents,
      });
      const isFailed = res?.status === 'failed' || Boolean(res?.error) || (res?.failedCount > 0 && res?.successCount === 0);
      const isPartial = res?.status === 'partially_completed' || (res?.failedCount > 0 && res?.successCount > 0);
      const errMsg = res?.error || res?.items?.find((i: any) => i.error)?.error;

      if (isFailed) {
        const finalMsg = errMsg || 'Agent execution failed.';
        setError(finalMsg);
        toast({
          message: finalMsg,
          type: 'error',
        });
      } else if (isPartial) {
        const warnMsg = `Agent completed with issues: ${res?.successCount ?? 0} succeeded, ${res?.failedCount ?? 0} failed.${errMsg ? ` Error: ${errMsg}` : ''}`;
        setError(warnMsg);
        toast({
          message: warnMsg,
          type: 'warning',
        });
      } else {
        const count = res?.totalItems ?? res?.items?.length ?? 0;
        toast({
          message: `Agent executed successfully${count ? ` (${count} items processed)` : ''}${suppressEvents ? ' (this step only)' : ''}`,
          type: 'success',
        });
      }
      await fetchStatuses();
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.items?.find((i: any) => i.error)?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Agent execution failed.';
      setError(errMsg);
      toast({
        message: errMsg,
        type: 'error',
      });
      await fetchStatuses();
    } finally {
      setExecutingWhole(false);
    }
  };

  const handleExecuteItem = async (row: any) => {
    if (!agentInstance.agentDefId || !agentInstance.id) return;
    setExecutingRowId(row.id);
    setError(null);
    const itemLabel = row.itemName || row.dataSourceItemId || row.id;
    try {
      const res = await apiContext.executeAgentInstance(agentInstance.agentDefId, agentInstance.id, {
        pipelineInstanceId: row.pipelineInstanceId,
        dataSourceDefId: row.dataSourceDefId,
        dataSourceInstanceId: row.dataSourceInstanceId,
        dataSourceItemId: row.dataSourceItemId || row.id,
        itemName: row.itemName,
        suppressEvents,
      });
      const isFailed = res?.status === 'failed' || Boolean(res?.error) || (res?.failedCount > 0 && res?.successCount === 0);
      const errMsg = res?.error || res?.items?.find((i: any) => i.error)?.error;

      if (isFailed) {
        const finalMsg = errMsg || `Item "${itemLabel}" execution failed.`;
        setError(finalMsg);
        toast({
          message: finalMsg,
          type: 'error',
        });
      } else {
        toast({
          message: `Item "${itemLabel}" executed successfully${suppressEvents ? ' (this step only)' : ''}`,
          type: 'success',
        });
      }
      await fetchStatuses();
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.items?.find((i: any) => i.error)?.error ||
        err?.response?.data?.message ||
        err?.message ||
        `Item "${itemLabel}" execution failed.`;
      setError(errMsg);
      toast({
        message: errMsg,
        type: 'error',
      });
      await fetchStatuses();
    } finally {
      setExecutingRowId(null);
    }
  };

  const { data: polledStatuses, error: pollError } = usePolling(
    async () => {
      if (agentInstance.agentDefId && agentInstance.id) {
        if (appliedItemName || appliedStatus) {
          return apiContext.searchAgentInstanceItemProcessingStatuses(
            agentInstance.agentDefId,
            agentInstance.id,
            appliedStatus ? { itemName: appliedItemName, statuses: [appliedStatus] } : { itemName: appliedItemName },
          );
        } else {
          return apiContext.getAgentInstanceItemProcessingStatuses(
            agentInstance.agentDefId,
            agentInstance.id,
          );
        }
      }
      return [];
    },
    { interval: 10000 },
  );

  useEffect(() => {
    if (agentInstance.id) {
      setLoading(true);
    }
  }, [agentInstance.id, agentInstance.agentDefId]);

  useEffect(() => {
    if (polledStatuses) {
      setItemProcessingStatuses(polledStatuses);
      setLoading(false);
    }
  }, [polledStatuses]);

  useEffect(() => {
    if (pollError) {
      setError(pollError);
    }
  }, [pollError]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 100,
      flex: 1,
      valueGetter: (value: any, row: any) => {
        return row.itemName ? row.itemName : `Group : ${row.dataSourceItemGroup}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      renderCell: (params: any) => {
        const status = params.value;
        if (status === 'PROCESSED') {
          return (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
              label="Processed"
              color="success"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, height: 24 }}
            />
          );
        } else if (status === 'PROCESSING') {
          return (
            <Chip
              icon={
                <AutorenewIcon
                  sx={{
                    fontSize: '16px !important',
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              }
              label="Processing"
              color="info"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, height: 24 }}
            />
          );
        } else if (status === 'FAILED') {
          return (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: '16px !important' }} />}
              label="Failed"
              color="error"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, height: 24 }}
            />
          );
        } else {
          return (
            <Chip
              icon={<HourglassEmptyIcon sx={{ fontSize: '16px !important' }} />}
              label={status || 'Scanned'}
              color="default"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500, height: 24 }}
            />
          );
        }
      },
    },
    {
      field: 'lastProcessedStartDate',
      headerName: 'Started',
      width: 170,
      valueGetter: (value: any, row: any) => {
        return value ? formatDate(value, dataTimeFormat) : '';
      },
    },
    {
      field: 'lastProcessedEndDate',
      headerName: 'Completed',
      width: 170,
      valueGetter: (value: any, row: any) => {
        return value ? formatDate(value, dataTimeFormat) : '';
      },
    },
    {
      field: 'eventName',
      headerName: 'Event Name',
      width: 120,
      flex: 1,
    },
    {
      field: 'dataSourceName',
      headerName: 'Datasource Name',
      width: 120,
      flex: 1,
    },
    {
      field: 'error',
      headerName: 'Error',
      width: 120,
      flex: 1,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params: any) => {
        const actions = [];
        actions.push(
          <GridActionsCellItem
            key="execute"
            icon={
              executingRowId === params.row.id ? (
                <CircularProgress size={16} />
              ) : (
                <PlayArrowIcon fontSize="small" color="primary" />
              )
            }
            label="Run Item"
            disabled={Boolean(executingRowId) || executingWhole}
            onClick={() => handleExecuteItem(params.row)}
          />,
        );
        if (viewProcesseItems) {
          actions.push(
            <GridActionsCellItem
              key="view"
              icon={<ViewIcon />}
              label="View"
              onClick={() => onOpenItem(params.row)}
            />,
          );
        }
        return actions;
      },
    },
  ];

  const searchProcessingStatusess = async () => {
    if (!itemName && !status) {
      return;
    }
    setError(null);
    try {
      setLoading(true);
      setAppliedItemName(itemName);
      setAppliedStatus(status);

      const itemProcessingStatuses = await apiContext.searchAgentInstanceItemProcessingStatuses(
        agentInstance.agentDefId,
        agentInstance.id,
        status ? { itemName, statuses: [status] } : { itemName },
      );

      setItemProcessingStatuses(itemProcessingStatuses);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilters = () => {
    setShowFiters(!showFiters);
  };

  const clearFilters = async () => {
    setItemName('');
    setStatus('');
    setAppliedItemName('');
    setAppliedStatus('');
    setError(null);
    try {
      setLoading(true);
      const itemProcessingStatuses = await apiContext.getAgentInstanceItemProcessingStatuses(
        agentInstance.agentDefId,
        agentInstance.id,
      );
      setItemProcessingStatuses(itemProcessingStatuses);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    if (!agentInstance.agentDefId || !agentInstance.id) return;
    setError(null);
    try {
      setLoading(true);
      let data;
      if (appliedItemName || appliedStatus) {
        data = await apiContext.searchAgentInstanceItemProcessingStatuses(
          agentInstance.agentDefId,
          agentInstance.id,
          appliedStatus ? { itemName: appliedItemName, statuses: [appliedStatus] } : { itemName: appliedItemName },
        );
      } else {
        data = await apiContext.getAgentInstanceItemProcessingStatuses(
          agentInstance.agentDefId,
          agentInstance.id,
        );
      }
      setItemProcessingStatuses(data || []);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack justifyContent="flex-start" spacing={2}>
      {error && <ErrorMessage error={error} onClose={() => setError(null)} />}
      <Box sx={{ overflowY: 'auto', p: 1, height: `${maxHeight}px` }}>
        <Grid container spacing={2} padding={1} ref={headerRef}>
          <Grid container justifyContent="flex-end" spacing={1} size={12}>
            {!showFiters && (
              <Grid alignSelf="center">
                <Button size="small" onClick={toggleFilters}>
                  Show filters
                </Button>
              </Grid>
            )}
            <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
            <Grid alignSelf="center">
              <Tooltip
                title={
                  suppressEvents
                    ? 'Run this step only: Executes this step without triggering subsequent steps in the pipeline.'
                    : 'Run and continue: Executes this step and automatically triggers subsequent steps in the pipeline.'
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={suppressEvents}
                      onChange={(e) => setSuppressEvents(e.target.checked)}
                      disabled={executingWhole || Boolean(executingRowId)}
                      sx={{
                        p: 0.5,
                        '&.Mui-checked': { color: 'primary.main' },
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: suppressEvents ? 'primary.main' : 'text.secondary',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Run this step only
                    </Typography>
                  }
                  sx={{ mr: 0.5, ml: 0 }}
                />
              </Tooltip>
            </Grid>
            <Grid alignSelf="center">
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={
                  executingWhole ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: 16 }} />
                  )
                }
                disabled={executingWhole || Boolean(executingRowId)}
                onClick={handleExecuteWhole}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderRadius: '6px',
                  height: 30,
                  px: 1.5,
                }}
              >
                {executingWhole ? 'Running Agent...' : 'Run Agent'}
              </Button>
            </Grid>
            <Grid alignSelf="center">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchStatuses}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      borderColor: 'secondary.main',
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          {showFiters && (
            <Grid container justifyContent="flex-start" spacing={1} size={12}>
              <Grid>
                <TextField label="File name" value={itemName} required maxLength={100} onChange={setItemName} />
              </Grid>
              <Grid size="grow">
                <SelectField options={ITEM_STATUSES} label="Status" value={status} autoFocus onChange={setStatus} />
              </Grid>
              <Grid alignSelf="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={!itemName && !status}
                  onClick={searchProcessingStatusess}
                  sx={{ mt: 2 }}
                >
                  Search
                </Button>
              </Grid>
              <Grid alignSelf="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!itemName && !status}
                  onClick={clearFilters}
                  sx={{ mt: 2 }}
                >
                  Clear
                </Button>
              </Grid>
              <Grid size="grow"></Grid>
              <Grid alignSelf="center">
                <Button size="small" onClick={toggleFilters}>
                  Hide filters
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        <DataGridTable
          rows={itemProcessingStatuses}
          columns={columns}
          loading={loading}
          height={`${maxHeight - headerSize.height - 20}px`}
        />
      </Box>
    </Stack>
  );
};

export default ItemProcessingStatuses;
